import React, { useState, useEffect } from 'react';
import {
  Layout,
  Button,
  Space,
  Table,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Tooltip,
  Tag,
  Drawer,
  Divider,
  Card,
  Typography,
  Popconfirm,
  Badge,
  Image
} from 'antd';
import {
  PlusOutlined,
  MoreOutlined,
  FilterOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  HolderOutlined,
  TableOutlined,
  AppstoreOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileSearchOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api, { tasksAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './DatabaseView.css';

const { Sider, Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const DatabaseView = () => {
  const { token } = useAuth();

  // File context / version history UI
  const [isFileContextVisible, setIsFileContextVisible] = useState(false);
  const [fileContextLoading, setFileContextLoading] = useState(false);
  const [fileContextError, setFileContextError] = useState(null);
  const [fileContextData, setFileContextData] = useState(null);
  const [selectedFileForContext, setSelectedFileForContext] = useState(null);

  const [isFileVersionsVisible, setIsFileVersionsVisible] = useState(false);
  const [fileVersionsLoading, setFileVersionsLoading] = useState(false);
  const [fileVersionsError, setFileVersionsError] = useState(null);
  const [fileVersionsData, setFileVersionsData] = useState([]);
  const [selectedFileForVersions, setSelectedFileForVersions] = useState(null);
  
  // State for tasks (rows)
  const [tasks, setTasks] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTaskRowKeys, setSelectedTaskRowKeys] = useState([]);
  const [selectedFileRowKeys, setSelectedFileRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [isBulkDeleteModalVisible, setIsBulkDeleteModalVisible] = useState(false);
  const [bulkDeleteSelection, setBulkDeleteSelection] = useState(null);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [statusActionType, setStatusActionType] = useState('task');
  const [selectedBulkStatus, setSelectedBulkStatus] = useState(undefined);
  
  // State for views and field visibility
  const defaultFieldOrder = ['id', 'title', 'description', 'status', 'priority', 'client', 'assigned_to', 'files', 'due_date', 'created_at', 'updated_at', 'archived'];
  
  // Load views from localStorage or use defaults
  const loadViewsFromStorage = () => {
    try {
      const savedViews = localStorage.getItem('databaseViewSettings');
      if (savedViews) {
        return JSON.parse(savedViews);
      }
    } catch (error) {
      console.error('Error loading views from localStorage:', error);
    }
    
    // Return default views if nothing saved
    return [
      { id: 1, name: 'All Tasks', filters: {}, hiddenFields: [], fieldOrder: defaultFieldOrder },
      { id: 2, name: 'Active', filters: { archived: false }, hiddenFields: [], fieldOrder: defaultFieldOrder },
      { id: 3, name: 'Completed', filters: { status: 'completed' }, hiddenFields: ['description'], fieldOrder: defaultFieldOrder },
      { id: 4, name: 'Overdue', filters: { overdue: true }, hiddenFields: [], fieldOrder: defaultFieldOrder }
    ];
  };

  const loadSelectedViewId = () => {
    try {
      const savedViewId = localStorage.getItem('databaseSelectedViewId');
      return savedViewId ? parseInt(savedViewId, 10) : 1;
    } catch (error) {
      return 1;
    }
  };

  const [views, setViews] = useState(loadViewsFromStorage());
  const initialSelectedViewId = loadSelectedViewId();
  const initialView = views.find(v => v.id === initialSelectedViewId) || views[0];
  const [selectedView, setSelectedView] = useState(initialView);
  const [hiddenFields, setHiddenFields] = useState(initialView.hiddenFields || []);
  const [fieldOrder, setFieldOrder] = useState(initialView.fieldOrder || defaultFieldOrder);
  
  // UI State
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskDrawerVisible, setIsTaskDrawerVisible] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'kanban'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedTaskRowKeys, setExpandedTaskRowKeys] = useState([]);
  
  // Forms
  const [form] = Form.useForm();
  const [viewForm] = Form.useForm();
  
  // Pagination & Filters
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status_id: undefined,
    priority: undefined,
    page: 1,
    per_page: 50
  });

  // Load data on mount and when filters change
  useEffect(() => {
    loadTaskStatuses();
    loadClients();
  }, []);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedView]);

  useEffect(() => {
    const visibleTaskIds = new Set(tasks.map((task) => task.id));
    const visibleFileIds = new Set(
      tasks.flatMap((task) => (task.files || []).map((file) => file.id))
    );

    setSelectedTaskRowKeys((prev) => prev.filter((taskId) => visibleTaskIds.has(taskId)));
    setSelectedFileRowKeys((prev) => prev.filter((fileId) => visibleFileIds.has(fileId)));
  }, [tasks]);

  useEffect(() => {
    if (viewMode !== 'grid') {
      setSelectedTaskRowKeys([]);
      setSelectedFileRowKeys([]);
      setExpandedTaskRowKeys([]);
    }
  }, [viewMode]);

  useEffect(() => {
    const searchTerm = (filters.search || '').trim().toLowerCase();

    if (!searchTerm || viewMode !== 'grid') {
      setExpandedTaskRowKeys([]);
      return;
    }

    const matchedTaskIds = tasks
      .filter((task) => {
        const files = (task?.files || []);
        return files.some((file) => {
          const parts = [file.original_filename, file.stored_filename, file.mime_type, file.file_status];
          return parts.some((v) => String(v || '').toLowerCase().includes(searchTerm));
        });
      })
      .map((task) => task.id);

    setExpandedTaskRowKeys(matchedTaskIds);
  }, [tasks, filters.search, viewMode]);

  // Save views to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('databaseViewSettings', JSON.stringify(views));
    } catch (error) {
      console.error('Error saving views to localStorage:', error);
    }
  }, [views]);

  // Save selected view ID to localStorage
  useEffect(() => {
    if (selectedView) {
      try {
        localStorage.setItem('databaseSelectedViewId', selectedView.id.toString());
      } catch (error) {
        console.error('Error saving selected view ID:', error);
      }
    }
  }, [selectedView]);

  // Save field order to current view
  useEffect(() => {
    if (selectedView) {
      setViews(prevViews =>
        prevViews.map(view =>
          view.id === selectedView.id
            ? { ...view, fieldOrder: fieldOrder }
            : view
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldOrder]);

  // Save hidden fields to current view
  useEffect(() => {
    if (selectedView) {
      setViews(prevViews =>
        prevViews.map(view =>
          view.id === selectedView.id
            ? { ...view, hiddenFields: hiddenFields }
            : view
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenFields]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      // Merge view filters with current filters
      const mergedFilters = {
        ...filters,
        ...selectedView.filters
      };
      
      const response = await tasksAPI.getTasks(mergedFilters);
      const data = response.data;
      setTasks(data.tasks || []);
      setPagination({
        current: data.pagination?.page || 1,
        pageSize: data.pagination?.per_page || 50,
        total: data.pagination?.total || 0
      });
    } catch (error) {
      message.error('Failed to load tasks');
      console.error('Load tasks error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTaskStatuses = async () => {
    try {
      const response = await api.get('/api/tasks/statuses');
      setTaskStatuses(response.data.statuses);
    } catch (error) {
      message.error('Failed to load task statuses');
    }
  };

  const loadClients = async () => {
    try {
      const response = await api.get('/api/tasks/clients');
      setClients(response.data.clients);
    } catch (error) {
      message.error('Failed to load clients');
    }
  };

  // Task CRUD operations
  const handleCreateTask = async (values) => {
    try {
      if (values.due_date) {
        values.due_date = values.due_date.toISOString();
      }
      await tasksAPI.createTask(values);
      message.success('Task created successfully');
      setIsTaskModalVisible(false);
      form.resetFields();
      loadTasks();
    } catch (error) {
      message.error('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, values) => {
    try {
      if (values.due_date) {
        values.due_date = values.due_date.toISOString();
      }
      await tasksAPI.updateTask(taskId, values);
      message.success('Task updated successfully');
      setIsTaskModalVisible(false);
      setEditingTask(null);
      form.resetFields();
      loadTasks();
    } catch (error) {
      message.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksAPI.deleteTask(taskId);
      message.success('Task deleted successfully');
      loadTasks();
    } catch (error) {
      message.error('Failed to delete task');
    }
  };

  const clearRowSelections = () => {
    setSelectedTaskRowKeys([]);
    setSelectedFileRowKeys([]);
  };

  const shouldIgnoreRowSelection = (event) => {
    const target = event?.target;
    return Boolean(
      target &&
      typeof target.closest === 'function' &&
      target.closest('button, a, input, textarea, .ant-btn, .ant-dropdown, .ant-select, .ant-picker, .ant-image, .ant-checkbox-wrapper')
    );
  };

  const toggleTaskRowSelection = (taskId) => {
    setSelectedFileRowKeys([]);
    setSelectedTaskRowKeys((prev) => (
      prev.includes(taskId)
        ? prev.filter((currentId) => currentId !== taskId)
        : [...prev, taskId]
    ));
  };

  const toggleFileRowSelection = (fileId) => {
    setSelectedTaskRowKeys([]);
    setSelectedFileRowKeys((prev) => (
      prev.includes(fileId)
        ? prev.filter((currentId) => currentId !== fileId)
        : [...prev, fileId]
    ));
  };

  const activeSelectionType = selectedFileRowKeys.length > 0 ? 'file' : selectedTaskRowKeys.length > 0 ? 'task' : null;
  const activeSelectionCount = activeSelectionType === 'file' ? selectedFileRowKeys.length : selectedTaskRowKeys.length;
  const activeSelectionLabel = activeSelectionType === 'file' ? 'file' : 'task';

  const openStatusModal = (type) => {
    const hasSelection = type === 'file' ? selectedFileRowKeys.length > 0 : selectedTaskRowKeys.length > 0;
    if (!hasSelection) {
      message.warning(`Select at least one ${type} first`);
      return;
    }

    setStatusActionType(type);
    setSelectedBulkStatus(undefined);
    setIsStatusModalVisible(true);
  };

  const applyBulkStatusChange = async () => {
    if (!selectedBulkStatus) {
      message.warning('Select a status first');
      return;
    }

    setBulkActionLoading(true);
    try {
      if (statusActionType === 'task') {
        await Promise.all(
          selectedTaskRowKeys.map((taskId) => tasksAPI.updateTask(taskId, { status_id: selectedBulkStatus }))
        );
        message.success(`Updated ${selectedTaskRowKeys.length} task(s)`);
      } else {
        await api.post('/api/task-files/bulk/status', {
          file_ids: selectedFileRowKeys,
          status: selectedBulkStatus
        });
        message.success(`Updated ${selectedFileRowKeys.length} file(s)`);
      }

      setIsStatusModalVisible(false);
      setSelectedBulkStatus(undefined);
      clearRowSelections();
      loadTasks();
    } catch (error) {
      message.error(`Failed to update ${statusActionType} status`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = () => {
    if (!activeSelectionType || activeSelectionCount === 0) {
      message.warning('Select at least one row first');
      return;
    }

    setBulkDeleteSelection({
      type: activeSelectionType,
      taskIds: [...selectedTaskRowKeys],
      fileIds: [...selectedFileRowKeys]
    });
    setIsBulkDeleteModalVisible(true);
  };

  const confirmBulkDelete = async () => {
    const selection = bulkDeleteSelection;
    if (!selection?.type) {
      setIsBulkDeleteModalVisible(false);
      return;
    }

    setBulkActionLoading(true);
    try {
      if (selection.type === 'task') {
        const deleteResults = await Promise.allSettled(
          selection.taskIds.map((taskId) => tasksAPI.deleteTask(taskId))
        );

        const deletedCount = deleteResults.filter((result) => result.status === 'fulfilled').length;
        const failedCount = deleteResults.length - deletedCount;

        if (deletedCount === 0) {
          throw new Error('Unable to delete selected tasks');
        }

        if (failedCount > 0) {
          message.warning(`Deleted ${deletedCount} task(s). Failed to delete ${failedCount} task(s).`);
        } else {
          message.success(`Deleted ${deletedCount} task(s)`);
        }
      } else {
        const response = await api.post('/api/task-files/bulk/delete', { file_ids: selection.fileIds });
        message.success(response.data?.message || `Deleted ${selection.fileIds.length} file(s)`);
      }

      setIsBulkDeleteModalVisible(false);
      setBulkDeleteSelection(null);
      clearRowSelections();
      loadTasks();
    } catch (error) {
      const failedType = selection.type === 'file' ? 'file' : 'task';
      message.error(`Failed to delete selected ${failedType}(s)`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleViewTask = async (taskId) => {
    try {
      const response = await api.get(`/api/tasks/${taskId}`);
      setSelectedTask(response.data.task);
      setIsTaskDrawerVisible(true);
    } catch (error) {
      message.error('Failed to load task details');
    }
  };

  const doesFileMatchSearch = (file, rawSearchTerm) => {
    const searchTerm = (rawSearchTerm || '').trim().toLowerCase();
    if (!searchTerm) {
      return true;
    }

    const searchableParts = [
      file.original_filename,
      file.stored_filename,
      file.mime_type,
      file.file_status
    ];

    return searchableParts.some((value) => String(value || '').toLowerCase().includes(searchTerm));
  };

  const getVisibleFilesForTask = (task, rawSearchTerm = filters.search) => {
    const allFiles = (task?.files || []).map((file) => ({
      ...file,
      task_id: task.id
    }));
    const searchTerm = (rawSearchTerm || '').trim();

    return searchTerm
      ? allFiles.filter((file) => doesFileMatchSearch(file, searchTerm))
      : allFiles;
  };

  // Field visibility
  const handleToggleFieldVisibility = (fieldKey) => {
    const newHiddenFields = hiddenFields.includes(fieldKey)
      ? hiddenFields.filter(key => key !== fieldKey)
      : [...hiddenFields, fieldKey];
    
    setHiddenFields(newHiddenFields);
  };

  // View management
  const handleCreateView = (values) => {
    const nextViewId = views.reduce((maxId, view) => Math.max(maxId, view.id), 0) + 1;
    const newView = {
      id: nextViewId,
      name: values.name,
      filters: {},
      hiddenFields: [],
      fieldOrder: defaultFieldOrder
    };
    setViews([...views, newView]);
    setSelectedView(newView);
    setHiddenFields([]);
    setFieldOrder(defaultFieldOrder);
    setIsViewModalVisible(false);
    viewForm.resetFields();
    message.success('View created successfully');
  };

  const handleDeleteView = (viewId) => {
    if (views.length <= 1) {
      message.warning('You must keep at least one view');
      return;
    }

    const remainingViews = views.filter(v => v.id !== viewId);
    if (remainingViews.length === views.length) return;

    setViews(remainingViews);

    if (selectedView?.id === viewId) {
      const nextView = remainingViews[0];
      setSelectedView(nextView);
      setHiddenFields(nextView.hiddenFields || []);
      setFieldOrder(nextView.fieldOrder || defaultFieldOrder);
    }

    message.success('View removed');
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    
    const name = status.name.toLowerCase();
    let icon = <ClockCircleOutlined />;
    if (name.includes('completed') || name.includes('ready')) {
      icon = <CheckCircleOutlined />;
    } else if (name.includes('progress') || name.includes('working')) {
      icon = <PlayCircleOutlined />;
    } else if (name.includes('blocked') || name.includes('testing')) {
      icon = <ExclamationCircleOutlined />;
    }

    return (
      <Badge 
        color={status.color || '#6B7280'} 
        text={
          <span style={{ color: status.color || '#6B7280' }}>
            {icon} {status.name}
          </span>
        }
      />
    );
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#ff4d4f',
      high: '#fa8c16',
      medium: '#1890ff',
      low: '#52c41a'
    };
    return colors[priority] || '#1890ff';
  };

  const getPreviewUrl = (fileId, size = 'thumbnail') => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
    return `${API_BASE_URL}/api/files/${fileId}/preview/${size}?token=${token}`;
  };

  const getVersionPreviewUrl = (fileId, version, size = 'thumbnail') => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
    return `${API_BASE_URL}/api/task-files/${fileId}/versions/${version}/preview/${size}?token=${token}`;
  };

  const parseFilenameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition) return null;
    // RFC 5987 (filename*=UTF-8''...)
    const utf8Match = contentDisposition.match(/filename\*=(?:UTF-8''|utf-8'')[^;]+/i);
    if (utf8Match) {
      const part = utf8Match[0];
      const value = part.split('=')[1];
      try {
        return decodeURIComponent(value.replace(/^UTF-8''/i, '').replace(/^utf-8''/i, '').replace(/"/g, '').trim());
      } catch (_) {
        return value.replace(/^UTF-8''/i, '').replace(/^utf-8''/i, '').replace(/"/g, '').trim();
      }
    }
    const match = contentDisposition.match(/filename="?([^";]+)"?/i);
    return match ? match[1] : null;
  };

  const downloadBlobResponse = async (response, fallbackFilename) => {
    const contentType = response.headers?.['content-type'] || '';
    const isJson = contentType.includes('application/json');
    if (isJson) {
      try {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.error || parsed?.message || 'Download failed');
      } catch (e) {
        throw e instanceof Error ? e : new Error('Download failed');
      }
    }

    const filename =
      parseFilenameFromContentDisposition(response.headers?.['content-disposition']) ||
      fallbackFilename ||
      'download';

    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => {
      try {
        window.URL.revokeObjectURL(blobUrl);
      } catch (_) {
        // ignore
      }
    }, 5000);
  };

  const extractAxiosBlobErrorMessage = async (err) => {
    const resp = err?.response;
    const data = resp?.data;
    const contentType = resp?.headers?.['content-type'] || '';
    if (data && typeof data === 'object' && typeof data.text === 'function' && contentType.includes('application/json')) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        return parsed?.error || parsed?.message || err?.message;
      } catch (_) {
        return err?.message;
      }
    }
    return resp?.data?.error || err?.message;
  };

  const downloadLatestTaskFile = async (file) => {
    if (!file?.id) return;
    try {
      const resp = await api.get(`/api/task-files/${file.id}/download`, { responseType: 'blob' });
      await downloadBlobResponse(resp, file.original_filename);
    } catch (err) {
      const msg = (await extractAxiosBlobErrorMessage(err)) || 'Failed to download file';
      message.error(msg);
    }
  };

  const downloadTaskFileVersion = async (file, versionRow) => {
    if (!file?.id || !versionRow?.version) return;
    try {
      const resp = await api.get(`/api/task-files/${file.id}/versions/${versionRow.version}/download`, { responseType: 'blob' });
      await downloadBlobResponse(resp, file.original_filename);
    } catch (err) {
      const msg = (await extractAxiosBlobErrorMessage(err)) || 'Failed to download version';
      message.error(msg);
    }
  };

  const openFileContext = async (file) => {
    setSelectedFileForContext(file);
    setIsFileContextVisible(true);
    setFileContextLoading(true);
    setFileContextError(null);
    setFileContextData(null);

    try {
      const res = await api.get(`/api/task-files/${file.id}/context`);
      setFileContextData(res.data);
    } catch (err) {
      setFileContextError(err?.response?.data?.error || err.message || 'Failed to load file context');
    } finally {
      setFileContextLoading(false);
    }
  };

  const openFileVersions = async (file) => {
    setSelectedFileForVersions(file);
    setIsFileVersionsVisible(true);
    setFileVersionsLoading(true);
    setFileVersionsError(null);
    setFileVersionsData([]);

    try {
      const res = await api.get(`/api/task-files/${file.id}/versions`);
      setFileVersionsData(res.data?.versions || []);
    } catch (err) {
      setFileVersionsError(err?.response?.data?.error || err.message || 'Failed to load version history');
    } finally {
      setFileVersionsLoading(false);
    }
  };

  // Render expanded row with task files
  const expandedRowRender = (record) => {
    const allFiles = (record.files || []).map((file) => ({
      ...file,
      task_id: record.id
    }));
    const files = getVisibleFilesForTask(record);
    
    if (allFiles.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
          <FileOutlined /> No files attached to this task
        </div>
      );
    }

    if (files.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
          <FileSearchOutlined /> No files match the current search
        </div>
      );
    }

    const fileColumns = [
      {
        title: 'Preview',
        dataIndex: 'id',
        key: 'preview',
        width: 80,
        render: (fileId, file) => {
          // Check if file has preview metadata from database
          let hasPreview = false;
          if (file.preview_metadata) {
            try {
              const previewMeta = typeof file.preview_metadata === 'string'
                ? JSON.parse(file.preview_metadata)
                : file.preview_metadata;
              // Check if preview data exists (either local or storage previews)
              hasPreview = previewMeta && (
                (previewMeta.local_previews && Object.keys(previewMeta.local_previews).length > 0) ||
                (previewMeta.storage_previews && Object.keys(previewMeta.storage_previews).length > 0) ||
                (previewMeta.db_previews && Object.keys(previewMeta.db_previews).length > 0)
              );
            } catch (e) {
              console.log('Error parsing preview metadata:', e);
            }
          }
          
          // Only show image preview if preview_metadata exists with actual preview data
          if (hasPreview) {
            return (
              <Image
                src={getPreviewUrl(fileId, 'thumbnail')}
                alt={file.original_filename}
                width={50}
                height={50}
                style={{ objectFit: 'cover', borderRadius: '4px' }}
                preview={{
                  src: getPreviewUrl(fileId, 'large')
                }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
              />
            );
          }
          
          // Show file icon for files without previews
          return <FileOutlined style={{ fontSize: '24px', color: '#999' }} />;
        }
      },
      {
        title: 'Filename',
        dataIndex: 'original_filename',
        key: 'filename',
        render: (text, file) => (
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {file.stored_filename}
            </div>
          </div>
        )
      },
      {
        title: 'Type',
        dataIndex: 'mime_type',
        key: 'mime_type',
        width: 150,
        render: (text) => <Tag>{text || 'Unknown'}</Tag>
      },
      {
        title: 'Size',
        dataIndex: 'file_size',
        key: 'file_size',
        width: 100,
        render: (size) => {
          if (!size) return '-';
          const kb = size / 1024;
          if (kb < 1024) return `${kb.toFixed(2)} KB`;
          const mb = kb / 1024;
          return `${mb.toFixed(2)} MB`;
        }
      },
      {
        title: 'Status',
        dataIndex: 'file_status',
        key: 'file_status',
        width: 100,
        render: (status) => {
          const colors = {
            pending: 'orange',
            processing: 'blue',
            completed: 'green',
            failed: 'red'
          };
          return <Tag color={colors[status] || 'default'}>{status || 'unknown'}</Tag>;
        }
      },
      {
        title: 'Storage',
        dataIndex: 'storage_type',
        key: 'storage_type',
        width: 120,
        render: (type) => <Tag color="cyan">{type || 'local'}</Tag>
      },
      {
        title: 'Version',
        dataIndex: 'version',
        key: 'version',
        width: 80
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        render: (date) => date ? moment(date).format('MMM DD, YYYY HH:mm') : '-'
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_, file) => (
          <Space size="small">
            <Tooltip title="Download latest">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadLatestTaskFile(file)}
              />
            </Tooltip>
            <Tooltip title="View context">
              <Button
                size="small"
                icon={<FileSearchOutlined />}
                onClick={() => openFileContext(file)}
              />
            </Tooltip>
            <Tooltip title="Version history">
              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => openFileVersions(file)}
              />
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div style={{ padding: '0 24px 16px' }}>
        <Table
          columns={fileColumns}
          dataSource={files}
          rowKey="id"
          rowSelection={{
            selectedRowKeys: selectedFileRowKeys,
            onChange: (nextSelectedRowKeys) => {
              setSelectedTaskRowKeys([]);
              setSelectedFileRowKeys(nextSelectedRowKeys);
            }
          }}
          rowClassName={(file) => (
            selectedFileRowKeys.includes(file.id)
              ? 'database-selectable-row database-selectable-row-selected'
              : 'database-selectable-row'
          )}
          onRow={(file) => ({
            onClick: (event) => {
              if (shouldIgnoreRowSelection(event)) {
                return;
              }
              toggleFileRowSelection(file.id);
            }
          })}
          pagination={false}
          size="small"
          bordered
        />
      </div>
    );
  };

  // Define all available fields
  const allFields = [
    { key: 'id', name: 'ID', type: 'number', width: 80 },
    { key: 'title', name: 'Title', type: 'text', width: 250, primary: true },
    { key: 'description', name: 'Description', type: 'long_text', width: 300 },
    { key: 'status', name: 'Status', type: 'select', width: 150 },
    { key: 'priority', name: 'Priority', type: 'select', width: 120 },
    { key: 'client', name: 'Client', type: 'select', width: 150 },
    { key: 'assigned_to', name: 'Assigned To', type: 'user', width: 150 },
    { key: 'files', name: 'Files', type: 'file', width: 200 },
    { key: 'due_date', name: 'Due Date', type: 'date', width: 150 },
    { key: 'created_at', name: 'Created', type: 'datetime', width: 150 },
    { key: 'updated_at', name: 'Updated', type: 'datetime', width: 150 },
    { key: 'archived', name: 'Archived', type: 'boolean', width: 100 }
  ];

  // Handle field reordering with drag and drop
  const [draggedFieldKey, setDraggedFieldKey] = useState(null);
  const [dragOverFieldKey, setDragOverFieldKey] = useState(null);

  const handleDragStart = (e, fieldKey) => {
    setDraggedFieldKey(fieldKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e, targetFieldKey) => {
    e.preventDefault();
    
    if (!draggedFieldKey || draggedFieldKey === targetFieldKey) {
      return;
    }

    setDragOverFieldKey(targetFieldKey);

    // Update the order live during drag
    const newOrder = [...fieldOrder];
    const draggedIndex = newOrder.indexOf(draggedFieldKey);
    const targetIndex = newOrder.indexOf(targetFieldKey);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged item
      newOrder.splice(draggedIndex, 1);
      // Insert at target position
      newOrder.splice(targetIndex, 0, draggedFieldKey);
      setFieldOrder(newOrder);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetFieldKey) => {
    e.preventDefault();
    setDraggedFieldKey(null);
    setDragOverFieldKey(null);
  };

  const handleDragEnd = () => {
    setDraggedFieldKey(null);
    setDragOverFieldKey(null);
  };

  // Build columns based on visible fields
  const buildColumns = () => {
    // Sort fields by the custom order
    const orderedFields = fieldOrder
      .map(key => allFields.find(field => field.key === key))
      .filter(field => field && !hiddenFields.includes(field.key));
    
    return orderedFields.map(field => {
      const column = {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{field.name}</span>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="hide"
                    icon={<EyeInvisibleOutlined />}
                    onClick={() => handleToggleFieldVisibility(field.key)}
                  >
                    Hide Field
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button type="text" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          </div>
        ),
        dataIndex: field.key,
        key: field.key,
        width: field.width
      };

      // Add filters based on field type
      if (field.key === 'status') {
        column.filters = taskStatuses.map(status => ({
          text: status.name,
          value: status.id
        }));
        column.filterMultiple = true;
        column.onFilter = (value, record) => record.status?.id === value;
      } else if (field.key === 'priority') {
        column.filters = [
          { text: 'Low', value: 'low' },
          { text: 'Medium', value: 'medium' },
          { text: 'High', value: 'high' },
          { text: 'Critical', value: 'critical' }
        ];
        column.filterMultiple = true;
        column.onFilter = (value, record) => record.priority === value;
      } else if (field.key === 'client') {
        column.filters = clients.map(client => ({
          text: client.name,
          value: client.id
        }));
        column.filterMultiple = true;
        column.onFilter = (value, record) => record.client?.id === value;
      } else if (field.key === 'archived') {
        column.filters = [
          { text: 'Archived', value: true },
          { text: 'Active', value: false }
        ];
        column.filterMultiple = false;
        column.onFilter = (value, record) => record.archived === value;
      }

      // Add custom sorting functions for specific fields
      if (field.key === 'id') {
        column.sorter = (a, b) => a.id - b.id;
      } else if (field.key === 'title') {
        column.sorter = (a, b) => (a.title || '').localeCompare(b.title || '');
      } else if (field.key === 'priority') {
        const priorityOrder = { low: 1, medium: 2, high: 3, critical: 4 };
        column.sorter = (a, b) => (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
      } else if (field.key === 'status') {
        column.sorter = (a, b) => (a.status?.name || '').localeCompare(b.status?.name || '');
      } else if (field.key === 'client') {
        column.sorter = (a, b) => (a.client?.name || '').localeCompare(b.client?.name || '');
      } else if (field.key === 'due_date' || field.key === 'created_at' || field.key === 'updated_at') {
        column.sorter = (a, b) => {
          const dateA = a[field.key] ? new Date(a[field.key]) : new Date(0);
          const dateB = b[field.key] ? new Date(b[field.key]) : new Date(0);
          return dateA - dateB;
        };
      } else if (field.key === 'file_count' || field.key === 'files') {
        column.sorter = (a, b) => (a.file_count || 0) - (b.file_count || 0);
      }

      // Custom renderers based on field type
      switch (field.key) {
        case 'id':
          column.render = (value) => <Text type="secondary">#{value}</Text>;
          break;
          
        case 'title':
          column.render = (text, record) => (
            <div>
              <Button type="link" onClick={() => handleViewTask(record.id)} style={{ padding: 0 }}>
                {text}
              </Button>
              {record.archived && (
                <Tag color="orange" size="small" style={{ marginLeft: '8px' }}>
                  ARCHIVED
                </Tag>
              )}
            </div>
          );
          break;
          
        case 'description':
          column.render = (text) => text ? (
            <Tooltip title={text}>
              <Text ellipsis>{text.substring(0, 100)}</Text>
            </Tooltip>
          ) : <Text type="secondary">No description</Text>;
          break;
          
        case 'status':
          column.render = (status) => getStatusBadge(status);
          break;
          
        case 'priority':
          column.render = (priority) => priority ? (
            <Tag color={getPriorityColor(priority)}>
              {priority.toUpperCase()}
            </Tag>
          ) : <Text type="secondary">None</Text>;
          break;
          
        case 'client':
          column.render = (client) => client ? client.name : <Text type="secondary">No client</Text>;
          break;
          
        case 'assigned_to':
          column.render = (_, record) => record.assigned_to_username || <Text type="secondary">Unassigned</Text>;
          break;
          
        case 'files':
          column.render = (files, record) => {
            const visibleFiles = getVisibleFilesForTask(record);
            const fileCount = visibleFiles.length;
            if (fileCount === 0) {
              return <Text type="secondary"><FileOutlined /> 0</Text>;
            }
            
            const firstFile = visibleFiles[0];
            let hasPreview = false;
            if (firstFile.preview_metadata) {
              try {
                const previewMeta = typeof firstFile.preview_metadata === 'string'
                  ? JSON.parse(firstFile.preview_metadata)
                  : firstFile.preview_metadata;
                hasPreview = previewMeta && (previewMeta.local_previews || previewMeta.storage_previews);
              } catch (e) {}
            }
            
            return (
              <Space>
                {hasPreview && (
                  <Image
                    src={getPreviewUrl(firstFile.id, 'thumbnail')}
                    alt={firstFile.original_filename}
                    width={32}
                    height={32}
                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                    preview={{
                      src: getPreviewUrl(firstFile.id, 'large')
                    }}
                  />
                )}
                <Text><FileOutlined /> {fileCount}</Text>
              </Space>
            );
          };
          break;
          
        case 'due_date':
          column.render = (date) => {
            if (!date) return <Text type="secondary">No due date</Text>;
            const dueDate = moment(date);
            const isOverdue = dueDate.isBefore(moment());
            return (
              <span style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
                {dueDate.format('MMM DD, YYYY')}
              </span>
            );
          };
          break;
          
        case 'created_at':
        case 'updated_at':
          column.render = (date) => date ? moment(date).format('MMM DD, YYYY HH:mm') : '-';
          break;
          
        case 'archived':
          column.render = (archived) => archived ? (
            <Tag color="orange">YES</Tag>
          ) : (
            <Tag color="green">NO</Tag>
          );
          break;
          
        default:
          column.render = (value) => value || <Text type="secondary">-</Text>;
      }

      return column;
    });
  };

  const columns = [
    ...buildColumns(),
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewTask(record.id)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => {
                setEditingTask(record);
                form.setFieldsValue({
                  ...record,
                  due_date: record.due_date ? moment(record.due_date) : null,
                  status_id: record.status?.id,
                  client_id: record.client?.id
                });
                setIsTaskModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this task?"
            onConfirm={() => handleDeleteTask(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const fieldMenu = (
    <Menu style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {fieldOrder.map((fieldKey) => {
        const field = allFields.find(f => f.key === fieldKey);
        if (!field) return null;
        
        return (
          <Menu.Item
            key={field.key}
            style={{ 
              padding: '8px 12px',
              cursor: 'move',
              backgroundColor: draggedFieldKey === field.key 
                ? '#e6f7ff' 
                : dragOverFieldKey === field.key 
                  ? '#f0f0f0' 
                  : 'transparent',
              opacity: draggedFieldKey === field.key ? 0.5 : 1,
              borderLeft: dragOverFieldKey === field.key ? '3px solid #1890ff' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
            draggable
            onDragStart={(e) => handleDragStart(e, field.key)}
            onDragEnter={(e) => handleDragEnter(e, field.key)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, field.key)}
            onDragEnd={handleDragEnd}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <HolderOutlined style={{ cursor: 'grab', color: '#999' }} />
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFieldVisibility(field.key);
                }}
                style={{ cursor: 'pointer', flex: 1 }}
              >
                {hiddenFields.includes(field.key) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                {' '}{field.name}
              </span>
            </div>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  return (
    <Layout style={{ height: '100%', background: '#fff' }}>
      {/* Sidebar with views */}
      <Sider 
        width={250} 
        collapsedWidth={0}
        collapsed={sidebarCollapsed}
        theme="light" 
        style={{ 
          borderRight: '1px solid #f0f0f0',
          overflow: 'hidden',
          transition: 'all 0.2s'
        }}
      >
        <div style={{ padding: 16 }}>
          <Title level={5}>Task Database</Title>
          
          <Divider style={{ margin: '12px 0' }}>Views</Divider>
          
          <div style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            {views.map(view => (
              <Card
                key={view.id}
                size="small"
                hoverable
                className={selectedView?.id === view.id ? 'selected-table' : ''}
                onClick={() => {
                  setSelectedView(view);
                  setHiddenFields(view.hiddenFields || []);
                  setFieldOrder(view.fieldOrder || defaultFieldOrder);
                }}
                style={{
                  marginBottom: 8,
                  border: selectedView?.id === view.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>{view.name}</Text>
                  <Space size="small">
                    <Badge count={pagination.total} style={{ backgroundColor: '#52c41a' }} />
                    <Popconfirm
                      title="Remove this view?"
                      description="This only removes the saved view settings."
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                      onConfirm={(e) => {
                        if (e?.stopPropagation) e.stopPropagation();
                        handleDeleteView(view.id);
                      }}
                      onCancel={(e) => {
                        if (e?.stopPropagation) e.stopPropagation();
                      }}
                      disabled={views.length <= 1}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={views.length <= 1}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
          
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setIsViewModalVisible(true)}
            block
            style={{ marginTop: 16 }}
          >
            New View
          </Button>
        </div>
      </Sider>

      {/* Main content */}
      <Layout>
        {/* Toolbar */}
        <div style={{
          padding: '12px 24px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fafafa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Space>
            <Button 
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              type="text"
            />
            <Title level={4} style={{ margin: 0 }}>Tasks - {selectedView.name}</Title>
            <Button.Group>
              <Button 
                icon={<TableOutlined />} 
                type={viewMode === 'grid' ? 'primary' : 'default'}
                onClick={() => setViewMode('grid')}
              >
                Grid
              </Button>
              <Button 
                icon={<AppstoreOutlined />} 
                type={viewMode === 'kanban' ? 'primary' : 'default'}
                onClick={() => setViewMode('kanban')}
              >
                Kanban
              </Button>
            </Button.Group>
          </Space>

          <Space>
            <Input
              placeholder="Search tasks..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={{ width: 200 }}
              prefix={<FilterOutlined />}
            />
            
            <Select
              placeholder="Status"
              value={filters.status_id}
              onChange={(value) => setFilters({ ...filters, status_id: value })}
              allowClear
              style={{ width: 150 }}
            >
              {taskStatuses.map(status => (
                <Option key={status.id} value={status.id}>{status.name}</Option>
              ))}
            </Select>
            
            <Select
              placeholder="Priority"
              value={filters.priority}
              onChange={(value) => setFilters({ ...filters, priority: value })}
              allowClear
              style={{ width: 120 }}
            >
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="critical">Critical</Option>
            </Select>
            
            <Dropdown overlay={fieldMenu}>
              <Button icon={<EyeOutlined />}>
                Fields ({allFields.length - hiddenFields.length}/{allFields.length})
              </Button>
            </Dropdown>
            
            <Button icon={<ReloadOutlined />} onClick={loadTasks}>
              Refresh
            </Button>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTask(null);
                form.resetFields();
                setIsTaskModalVisible(true);
              }}
            >
              New Task
            </Button>
          </Space>
        </div>

        {/* Data grid or Kanban view */}
        <Content style={{ padding: 24, paddingBottom: activeSelectionType ? 136 : 24, overflow: 'auto' }}>
          {viewMode === 'grid' ? (
            <Table
              dataSource={tasks}
              columns={columns}
              loading={loading}
              rowKey="id"
              rowSelection={{
                selectedRowKeys: selectedTaskRowKeys,
                onChange: (nextSelectedRowKeys) => {
                  setSelectedFileRowKeys([]);
                  setSelectedTaskRowKeys(nextSelectedRowKeys);
                }
              }}
              rowClassName={(task) => (
                selectedTaskRowKeys.includes(task.id)
                  ? 'database-selectable-row database-selectable-row-selected'
                  : 'database-selectable-row'
              )}
              onRow={(task) => ({
                onClick: (event) => {
                  if (shouldIgnoreRowSelection(event)) {
                    return;
                  }
                  toggleTaskRowSelection(task.id);
                }
              })}
              expandable={{
                expandedRowRender,
                expandedRowKeys: expandedTaskRowKeys,
                onExpandedRowsChange: (nextExpandedRowKeys) => setExpandedTaskRowKeys(nextExpandedRowKeys),
                rowExpandable: (record) => getVisibleFilesForTask(record).length > 0,
                expandRowByClick: false,
                expandIcon: ({ expanded, onExpand, record }) => 
                  getVisibleFilesForTask(record).length > 0 ? (
                    expanded ? (
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<span>▼</span>} 
                        onClick={(e) => {
                          e.stopPropagation();
                          onExpand(record, e);
                        }}
                        style={{ marginRight: 8 }}
                      />
                    ) : (
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<span>▶</span>} 
                        onClick={(e) => {
                          e.stopPropagation();
                          onExpand(record, e);
                        }}
                        style={{ marginRight: 8 }}
                      />
                    )
                  ) : <span style={{ width: 32, display: 'inline-block' }} />
              }}
              onChange={(paginationConfig) => {
                // Handle pagination changes
                if (paginationConfig.current !== pagination.current || paginationConfig.pageSize !== pagination.pageSize) {
                  setFilters(prev => ({
                    ...prev,
                    page: paginationConfig.current,
                    per_page: paginationConfig.pageSize
                  }));
                }
              }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} tasks`
              }}
              scroll={{ x: 'max-content' }}
              bordered
            />
          ) : (
            <div style={{ display: 'flex', gap: '16px', height: '100%', overflowX: 'auto' }}>
              {taskStatuses.map(status => {
                const statusTasks = tasks.filter(task => task.status?.id === status.id);
                
                return (
                  <div 
                    key={status.id} 
                    style={{ 
                      minWidth: '300px', 
                      maxWidth: '350px', 
                      flex: '0 0 auto',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ 
                      marginBottom: '16px', 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Badge color={status.color || '#6B7280'} />
                        {status.name}
                      </Title>
                      <Badge count={statusTasks.length} style={{ backgroundColor: status.color || '#6B7280' }} />
                    </div>
                    
                    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {statusTasks.map(task => (
                        <Card
                          key={task.id}
                          size="small"
                          hoverable
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleViewTask(task.id)}
                          bodyStyle={{ padding: '12px' }}
                        >
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <Text strong style={{ fontSize: '14px', flex: 1 }}>
                                {task.title}
                              </Text>
                              {task.priority && (
                                <Tag color={getPriorityColor(task.priority)} style={{ margin: 0 }}>
                                  {task.priority.toUpperCase()}
                                </Tag>
                              )}
                            </div>
                            
                            {task.description && (
                              <Text 
                                type="secondary" 
                                ellipsis={{ rows: 2 }}
                                style={{ fontSize: '12px' }}
                              >
                                {task.description}
                              </Text>
                            )}
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                              <Space size="small">
                                {task.client && (
                                  <Tag style={{ fontSize: '11px' }}>{task.client.name}</Tag>
                                )}
                                {task.file_count > 0 && (
                                  <Text type="secondary" style={{ fontSize: '11px' }}>
                                    <FileOutlined /> {task.file_count}
                                  </Text>
                                )}
                              </Space>
                              
                              {task.due_date && (
                                <Text 
                                  type={moment(task.due_date).isBefore(moment()) ? 'danger' : 'secondary'}
                                  style={{ fontSize: '11px' }}
                                >
                                  {moment(task.due_date).format('MMM DD')}
                                </Text>
                              )}
                            </div>
                          </Space>
                        </Card>
                      ))}
                      
                      {statusTasks.length === 0 && (
                        <div style={{ 
                          padding: '24px', 
                          textAlign: 'center', 
                          color: '#999',
                          border: '2px dashed #d9d9d9',
                          borderRadius: '4px'
                        }}>
                          No tasks
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Content>
      </Layout>

      {activeSelectionType && (
        <div className="database-selection-toolbar">
          <div className="database-selection-toolbar__summary">
            <Text strong>{activeSelectionCount} {activeSelectionLabel}{activeSelectionCount === 1 ? '' : 's'} selected</Text>
            <Button type="link" onClick={clearRowSelections}>
              Clear selection
            </Button>
          </div>
          <div className="database-selection-toolbar__actions">
            <Button
              type="text"
              className="database-selection-toolbar__action"
              onClick={() => openStatusModal(activeSelectionType)}
              loading={bulkActionLoading}
            >
              <EditOutlined className="database-selection-toolbar__icon" />
              <span>Change Status</span>
            </Button>
            <Button
              type="text"
              danger
              className="database-selection-toolbar__action"
              onClick={handleBulkDelete}
              loading={bulkActionLoading}
            >
              <DeleteOutlined className="database-selection-toolbar__icon" />
              <span>Delete</span>
            </Button>
          </div>
        </div>
      )}

      <Modal
        title={`Delete ${bulkDeleteSelection?.type === 'file' ? 'file' : 'task'}${(bulkDeleteSelection?.type === 'file' ? bulkDeleteSelection?.fileIds?.length : bulkDeleteSelection?.taskIds?.length) > 1 ? 's' : ''}`}
        open={isBulkDeleteModalVisible}
        onCancel={() => {
          if (bulkActionLoading) {
            return;
          }
          setIsBulkDeleteModalVisible(false);
          setBulkDeleteSelection(null);
        }}
        onOk={confirmBulkDelete}
        confirmLoading={bulkActionLoading}
        okText="Delete"
        okType="danger"
      >
        <p>
          Are you sure you want to delete{' '}
          {(bulkDeleteSelection?.type === 'file' ? bulkDeleteSelection?.fileIds?.length : bulkDeleteSelection?.taskIds?.length) || 0}{' '}
          {bulkDeleteSelection?.type === 'file' ? 'file(s)' : 'task(s)'}? This action cannot be undone.
        </p>
      </Modal>

      {/* Create/Edit Task Modal */}
      <Modal
        title={editingTask ? 'Edit Task' : 'Create New Task'}
        open={isTaskModalVisible}
        onCancel={() => {
          setIsTaskModalVisible(false);
          setEditingTask(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (editingTask) {
              handleUpdateTask(editingTask.id, values);
            } else {
              handleCreateTask(values);
            }
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="Task title" />
          </Form.Item>
          
          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Task description" />
          </Form.Item>
          
          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              name="status_id"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              style={{ flex: 1 }}
            >
              <Select placeholder="Select status">
                {taskStatuses.map(status => (
                  <Option key={status.id} value={status.id}>{status.name}</Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item name="priority" label="Priority" style={{ flex: 1 }}>
              <Select placeholder="Select priority">
                <Option value="low">Low</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
                <Option value="critical">Critical</Option>
              </Select>
            </Form.Item>
          </Space>
          
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="client_id" label="Client" style={{ flex: 1 }}>
              <Select placeholder="Select client" allowClear>
                {clients.map(client => (
                  <Option key={client.id} value={client.id}>{client.name}</Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item name="due_date" label="Due Date" style={{ flex: 1 }}>
              <DatePicker 
                style={{ width: '100%' }} 
                showTime
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* Create View Modal */}
      <Modal
        title="Create New View"
        open={isViewModalVisible}
        onCancel={() => {
          setIsViewModalVisible(false);
          viewForm.resetFields();
        }}
        onOk={() => viewForm.submit()}
      >
        <Form form={viewForm} layout="vertical" onFinish={handleCreateView}>
          <Form.Item
            name="name"
            label="View Name"
            rules={[{ required: true, message: 'Please enter a view name' }]}
          >
            <Input placeholder="e.g., My Tasks, Urgent, This Week" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Change ${statusActionType === 'task' ? 'Task' : 'File'} Status`}
        open={isStatusModalVisible}
        onCancel={() => {
          setIsStatusModalVisible(false);
          setSelectedBulkStatus(undefined);
        }}
        onOk={applyBulkStatusChange}
        okText="Apply"
        confirmLoading={bulkActionLoading}
      >
        <Form layout="vertical">
          <Form.Item label="Status" required>
            <Select
              placeholder={`Select ${statusActionType} status`}
              value={selectedBulkStatus}
              onChange={setSelectedBulkStatus}
            >
              {taskStatuses.map((status) => (
                <Option key={status.id} value={statusActionType === 'task' ? status.id : status.name}>{status.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* File Context Modal */}
      <Modal
        title={selectedFileForContext ? `File Context: ${selectedFileForContext.original_filename}` : 'File Context'}
        open={isFileContextVisible}
        onCancel={() => setIsFileContextVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsFileContextVisible(false)}>
            Close
          </Button>
        ]}
        width={900}
      >
        {fileContextLoading && <div>Loading…</div>}
        {fileContextError && <div style={{ color: '#ff4d4f' }}>{fileContextError}</div>}
        {!fileContextLoading && !fileContextError && fileContextData && (
          <div>
            <Divider orientation="left">Summary</Divider>
            <Space wrap>
              <Tag>File ID: {fileContextData.file?.id}</Tag>
              <Tag>Hash: {fileContextData.file?.unique_hash || '-'}</Tag>
              <Tag>Size: {fileContextData.file?.file_size || '-'}</Tag>
              <Tag>Status: {fileContextData.file?.status || '-'}</Tag>
              <Tag>Last Modified: {fileContextData.file?.last_modified || '-'}</Tag>
            </Space>

            <Divider orientation="left">upload_metadata</Divider>
            <pre style={{ maxHeight: 300, overflow: 'auto', background: '#0b1020', color: '#e5e7eb', padding: 12, borderRadius: 6 }}>
              {JSON.stringify(fileContextData.file?.upload_metadata || {}, null, 2)}
            </pre>

            <Divider orientation="left">preview_metadata</Divider>
            <pre style={{ maxHeight: 300, overflow: 'auto', background: '#0b1020', color: '#e5e7eb', padding: 12, borderRadius: 6 }}>
              {JSON.stringify(fileContextData.file?.preview_metadata || {}, null, 2)}
            </pre>
          </div>
        )}
      </Modal>

      {/* File Version History Modal */}
      <Modal
        title={selectedFileForVersions ? `Version History: ${selectedFileForVersions.original_filename}` : 'Version History'}
        open={isFileVersionsVisible}
        onCancel={() => setIsFileVersionsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsFileVersionsVisible(false)}>
            Close
          </Button>
        ]}
        width={1000}
      >
        {fileVersionsLoading && <div>Loading…</div>}
        {fileVersionsError && <div style={{ color: '#ff4d4f' }}>{fileVersionsError}</div>}
        {!fileVersionsLoading && !fileVersionsError && (
          <Table
            rowKey={(row) => `${row.is_current ? 'current' : 'archived'}_${row.version}`}
            size="small"
            pagination={false}
            dataSource={fileVersionsData}
            columns={[
              {
                title: 'Preview',
                key: 'preview',
                width: 90,
                render: (_, row) => {
                  const hasPreview = row.has_preview;
                  if (!hasPreview || !selectedFileForVersions) return <FileOutlined style={{ fontSize: 22, color: '#999' }} />;

                  const src = row.is_current
                    ? getPreviewUrl(selectedFileForVersions.id, 'thumbnail')
                    : getVersionPreviewUrl(selectedFileForVersions.id, row.version, 'thumbnail');
                  const largeSrc = row.is_current
                    ? getPreviewUrl(selectedFileForVersions.id, 'large')
                    : getVersionPreviewUrl(selectedFileForVersions.id, row.version, 'large');

                  return (
                    <Image
                      src={src}
                      width={60}
                      height={60}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      preview={{ src: largeSrc }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                    />
                  );
                }
              },
              {
                title: 'Version',
                dataIndex: 'version',
                key: 'version',
                width: 120,
                render: (v, row) => (
                  <Space>
                    <Tag color={row.is_current ? 'blue' : 'default'}>v{v}</Tag>
                    {row.is_current && <Tag color="green">Current</Tag>}
                  </Space>
                )
              },
              {
                title: 'Last Modified',
                dataIndex: 'last_modified',
                key: 'last_modified',
                width: 220,
                render: (val) => (val ? moment(val).format('MMM DD, YYYY HH:mm') : '-')
              },
              {
                title: 'Archived At',
                dataIndex: 'archived_at',
                key: 'archived_at',
                width: 220,
                render: (val, row) => (row.is_current ? '-' : (val ? moment(val).format('MMM DD, YYYY HH:mm') : '-'))
              },
              {
                title: 'Size',
                dataIndex: 'file_size',
                key: 'file_size',
                width: 120,
                render: (size) => {
                  if (!size) return '-';
                  const kb = size / 1024;
                  if (kb < 1024) return `${kb.toFixed(2)} KB`;
                  const mb = kb / 1024;
                  return `${mb.toFixed(2)} MB`;
                }
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 140,
                render: (s) => <Tag>{s || '-'}</Tag>
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 120,
                render: (_, row) => (
                  <Space size="small">
                    <Tooltip title={row.is_current ? 'Download current version' : 'Download this version'}>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadTaskFileVersion(selectedFileForVersions, row)}
                        disabled={!selectedFileForVersions}
                      />
                    </Tooltip>
                  </Space>
                )
              }
            ]}
          />
        )}
      </Modal>

      {/* Task Details Drawer */}
      <Drawer
        title="Task Details"
        placement="right"
        size="large"
        open={isTaskDrawerVisible}
        onClose={() => setIsTaskDrawerVisible(false)}
      >
        {selectedTask && (
          <div>
            <Title level={4}>{selectedTask.title}</Title>
            
            <Divider />
            
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Status:</Text>
                <div>{getStatusBadge(selectedTask.status)}</div>
              </div>
              
              <div>
                <Text type="secondary">Priority:</Text>
                <div>
                  {selectedTask.priority ? (
                    <Tag color={getPriorityColor(selectedTask.priority)}>
                      {selectedTask.priority.toUpperCase()}
                    </Tag>
                  ) : <Text type="secondary">None</Text>}
                </div>
              </div>
              
              {selectedTask.description && (
                <div>
                  <Text type="secondary">Description:</Text>
                  <div style={{ marginTop: 8 }}>{selectedTask.description}</div>
                </div>
              )}
              
              <div>
                <Text type="secondary">Client:</Text>
                <div>{selectedTask.client ? selectedTask.client.name : 'No client'}</div>
              </div>
              
              <div>
                <Text type="secondary">Assigned To:</Text>
                <div>{selectedTask.assigned_to_username || 'Unassigned'}</div>
              </div>
              
              <div>
                <Text type="secondary">Due Date:</Text>
                <div>{selectedTask.due_date ? moment(selectedTask.due_date).format('YYYY-MM-DD HH:mm') : 'No due date'}</div>
              </div>
              
              <div>
                <Text type="secondary">Created:</Text>
                <div>{moment(selectedTask.created_at).format('YYYY-MM-DD HH:mm')}</div>
              </div>
            </Space>
            
            {selectedTask.files && selectedTask.files.length > 0 && (
              <>
                <Divider />
                <Title level={5}>Files ({selectedTask.files.length})</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {selectedTask.files.map(file => {
                    // Check if file has preview metadata
                    // eslint-disable-next-line no-unused-vars
                    let hasPreview = false;
                    if (file.preview_metadata) {
                      try {
                        const previewMeta = typeof file.preview_metadata === 'string'
                          ? JSON.parse(file.preview_metadata)
                          : file.preview_metadata;
                        // eslint-disable-next-line no-unused-vars
                        hasPreview = previewMeta && (previewMeta.local_previews || previewMeta.storage_previews);
                      } catch (e) {
                        console.log('Error parsing preview metadata:', e);
                      }
                    }
                    
                    // Show preview if it's an image file
                    const isImage = file.mime_type && file.mime_type.startsWith('image/');
                    
                    return (
                      <Card key={file.id} size="small">
                        <Space>
                          {isImage && (
                            <Image
                              src={getPreviewUrl(file.id, 'thumbnail')}
                              alt={file.original_filename}
                              width={60}
                              height={60}
                              style={{ objectFit: 'cover', borderRadius: '4px' }}
                              preview={{
                                src: getPreviewUrl(file.id, 'large')
                              }}
                              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                            />
                          )}
                          <div>
                            <div><FileOutlined /> {file.original_filename}</div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {(file.file_size / 1024).toFixed(1)} KB • {file.storage_type}
                            </Text>
                          </div>
                        </Space>
                      </Card>
                    );
                  })}
                </Space>
              </>
            )}
          </div>
        )}
      </Drawer>
    </Layout>
  );
};

export default DatabaseView;
